import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Grid,
} from "@mui/material";
import { HexColorPicker } from "react-colorful";

const PRESET_COLORS = [
  // Purple/Violet shades
  "#5e35b1",
  "#9162e4",
  "#7b1fa2",
  "#aa00ff",
  // Blue shades
  "#1976d2",
  "#64b5f6",
  "#0d47a1",
  "#00b0ff",
  // Green shades
  "#2e7d32",
  "#66bb6a",
  "#00695c",
  "#00bfa5",
  // Red/Orange shades
  "#ff6f00",
  "#ffad42",
  "#d32f2f",
  "#ff5252",
  // Neutral shades
  "#455a64",
  "#78909c",
  "#263238",
  "#607d8b",
];

const ColorPickerDialog = ({
  open,
  onClose,
  initialColor,
  onColorSelect,
  title,
}) => {
  const [color, setColor] = useState(initialColor || "#ffffff");
  const [inputValue, setInputValue] = useState(initialColor || "#ffffff");
  const [isValidHex, setIsValidHex] = useState(true);

  useEffect(() => {
    if (open) {
      setColor(initialColor || "#ffffff");
      setInputValue(initialColor || "#ffffff");
      validateHexColor(initialColor || "#ffffff");
    }
  }, [open, initialColor]);

  const validateHexColor = (hex) => {
    const isValid = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex);
    setIsValidHex(isValid);
    return isValid;
  };

  const handleColorChange = (newColor) => {
    setColor(newColor);
    setInputValue(newColor);
    validateHexColor(newColor);
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    // Only update the picker if it's a valid hex
    if (validateHexColor(value)) {
      setColor(value);
    }
  };

  const handleSubmit = () => {
    if (isValidHex) {
      onColorSelect(color);
      onClose();
    }
  };

  const handlePresetSelect = (presetColor) => {
    setColor(presetColor);
    setInputValue(presetColor);
    setIsValidHex(true);
  };

  // Color comparison function to find similar colors
  const findSimilarColors = (targetColor) => {
    if (!isValidHex) return [];

    // Convert hex to RGB
    const hexToRgb = (hex) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return { r, g, b };
    };

    // Calculate color distance (simple Euclidean distance)
    const colorDistance = (color1, color2) => {
      const rgb1 = hexToRgb(color1);
      const rgb2 = hexToRgb(color2);
      return Math.sqrt(
        Math.pow(rgb1.r - rgb2.r, 2) +
          Math.pow(rgb1.g - rgb2.g, 2) +
          Math.pow(rgb1.b - rgb2.b, 2),
      );
    };

    // Sort colors by similarity
    return [...PRESET_COLORS]
      .sort((a, b) => {
        return colorDistance(a, targetColor) - colorDistance(b, targetColor);
      })
      .slice(0, 4); // Get the 4 most similar colors
  };

  const similarColors = findSimilarColors(color);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{title || "Choose a Color"}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, my: 1 }}>
          {/* Color Picker */}
          <Box
            sx={{ width: "100%", display: "flex", justifyContent: "center" }}
          >
            <HexColorPicker
              color={color}
              onChange={handleColorChange}
              style={{ width: "100%", maxWidth: "240px" }}
            />
          </Box>

          {/* Hex Input */}
          <TextField
            fullWidth
            label="Hex Color"
            value={inputValue}
            onChange={handleInputChange}
            error={!isValidHex}
            helperText={!isValidHex ? "Invalid hex color format" : ""}
            placeholder="#RRGGBB"
            variant="outlined"
            size="small"
            sx={{ mt: 2 }}
            InputProps={{
              startAdornment: (
                <Box
                  sx={{
                    width: 24,
                    height: 24,
                    borderRadius: 1,
                    mr: 1,
                    border: "1px solid #ccc",
                    backgroundColor: isValidHex ? color : "#ccc",
                  }}
                />
              ),
            }}
          />

          {/* Color Presets */}
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" sx={{ mb: 1, color: "text.secondary" }}>
              Preset Colors
            </Typography>
            <Grid container spacing={1}>
              {PRESET_COLORS.slice(0, 12).map((presetColor, index) => (
                <Grid item key={index}>
                  <Box
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: "4px",
                      backgroundColor: presetColor,
                      cursor: "pointer",
                      border:
                        presetColor === color
                          ? "2px solid black"
                          : "1px solid #ccc",
                      "&:hover": {
                        opacity: 0.8,
                        transform: "scale(1.1)",
                      },
                    }}
                    onClick={() => handlePresetSelect(presetColor)}
                    title={presetColor}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Similar Colors */}
          {isValidHex && (
            <Box sx={{ mt: 1 }}>
              <Typography
                variant="body2"
                sx={{ mb: 1, color: "text.secondary" }}
              >
                Similar Colors
              </Typography>
              <Grid container spacing={1}>
                {similarColors.map((similarColor, index) => (
                  <Grid item key={index}>
                    <Box
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: "4px",
                        backgroundColor: similarColor,
                        cursor: "pointer",
                        border:
                          similarColor === color
                            ? "2px solid black"
                            : "1px solid #ccc",
                        "&:hover": {
                          opacity: 0.8,
                          transform: "scale(1.1)",
                        },
                      }}
                      onClick={() => handlePresetSelect(similarColor)}
                      title={similarColor}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Color Preview */}
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 1,
              backgroundColor: isValidHex ? color : "#ccc",
              height: 50,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              variant="body1"
              sx={{
                color: isValidHex
                  ? getBrightness(color) > 128
                    ? "#000"
                    : "#fff"
                  : "#fff",
              }}
            >
              {isValidHex ? inputValue : "Invalid color"}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!isValidHex}
        >
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Helper function to determine text color
function getBrightness(hexColor) {
  // Convert hex to RGB
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);

  // Calculate brightness using a common formula
  return (r * 299 + g * 587 + b * 114) / 1000;
}

export default ColorPickerDialog;
